
import { PulseHmac } from './pulseHmac.js';
import PulseAxios from 'axios';
import UuidGenerator from 'uuid';
import { isDefined } from './isDefined.js';
import { isNullOrWhiteSpace } from './isNullOrWhiteSpace';
import { PulseApiException } from '../exceptions/pulseApiException.js';
import axiosRetry from 'axios-retry';
import { MD5 } from 'object-hash';

const currentlyExecutingApiCalls = new Map();

export class PulseApi {
    constructor(authId, secret, baseUrl, timeout, retryOptions, emToken) {
        this.authId = authId;
        this.secret = secret;
        this.baseUrl = baseUrl;
        this.timeout = timeout;
        this.retryOptions = retryOptions;
        this.emToken = emToken;
        this.apiDebouncingKey = baseUrl + authId + secret;

        if (!currentlyExecutingApiCalls.has(this.apiDebouncingKey)) {
            currentlyExecutingApiCalls.set(this.apiDebouncingKey, new Map());
        }

        this.pulseHmacProvider = new PulseHmac(this.authId, this.secret);
        this.pulseAxios = PulseAxios.create({
            baseURL: this.baseUrl,
            timeout: this.timeout
        });

        if (this.retryOptions?.enableRetry && this.retryOptions?.retryCount) {
            axiosRetry(this.pulseAxios, { retries: this.retryOptions.retryCount });
        }

        this.pulseAxios.interceptors.request.use((config) => {
  
            config.headers['X-RID'] = UuidGenerator();

            const { emToken } = config?.params || {};
            if (emToken && /\S/.test(emToken))
            {
                config.headers.Authorization = `Bearer ${emToken}`;
                config.withCredentials = true;
                delete config.params.emToken;
            }

            if (!config.hmac)
            {
                return config;
            }

            const hashedObj = this.pulseHmacProvider.getHashedURL(config.method, config.url, config.params, config.data);
            config.params.authid = hashedObj.authid;
            config.params.timestamp = hashedObj.timestamp;
            config.params.hash = hashedObj.hmac;
            
            return config;

        }, (error) => {
            return Promise.reject(error);
        });


        this.pulseAxios.interceptors.response.use((resp) => {
            return resp;
        }, (error) => {
            if (error.config) {
                var requestHeaders = [];
                var responseHeaders = [];
                var requestKeys = Object.keys(error.config.headers);
                requestKeys.sort(function (a, b) {
                    return a.toLowerCase().localeCompare(b.toLowerCase());
                });

                for (var i = 0; i < requestKeys.length; i++) {
                    var requestKey = requestKeys[i];
                    if (error.config.headers.hasOwnProperty(requestKey)) {
                        requestHeaders.push(requestKey + '=' + error.config.headers[requestKey]);
                    }
                }
            }

            if (error.response) {
                var responseKeys = Object.keys(error.response.headers);
                responseKeys.sort(function (a, b) {
                    return a.toLowerCase().localeCompare(b.toLowerCase());
                });

                for (var i = 0; i < responseKeys.length; i++) {
                    var responseKey = responseKeys[i];
                    if (error.response.headers.hasOwnProperty(responseKey)) {
                        responseHeaders.push(responseKey + '=' + error.response.headers[responseKey]);
                    }
                }
            }
            return Promise.reject(new PulseApiException(error));
        });
    }

    getData(...args) {
        const [ path, params, timeout, respType, hmac ] = args;
        const requestHash = MD5(args);
        // If this request is currently executing, return the promise that we saved to debounce the call.
        const apiCallsForThisUrl = currentlyExecutingApiCalls.get(this.apiDebouncingKey);
        if (apiCallsForThisUrl.has(requestHash)) {
            return apiCallsForThisUrl.get(requestHash);
        }

        apiCallsForThisUrl.set(requestHash, new Promise((resolve, reject) => {
            const headers = { };
            if (!isNullOrWhiteSpace(this.emToken) ) {
                headers['Authorization'] = `Bearer ${this.emToken}`;
            }
            this.pulseAxios.get(path, {
                params: params,
                timeout: timeout,
                responseType: respType,
                headers,
                hmac: hmac
            }).then((response) => {
                if (isDefined(response) && isDefined(response.status)) {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(response.data);
                    } else {
                        reject(new Error('Invalid PulseAPI response code: ' + response.status));
                    }
                } else {
                    reject(new Error('Invalid PulseAPI response: NULL'));
                }
            }).catch((error) => {
                reject(error);
            });
        }).finally(()=>{
            if (apiCallsForThisUrl.has(requestHash)) {
                apiCallsForThisUrl.delete(requestHash);
            }
        }));

        return apiCallsForThisUrl.get(requestHash);
    }

    postData(path, params, data, timeout, reqType, respType, hmac) {

        return new Promise((resolve, reject) => {

            const headers = { };

            if (reqType)
            {
                headers['Content-Type'] = reqType;
            }

            if (!isNullOrWhiteSpace(this.emToken) ) {
                headers['Authorization'] = `Bearer ${this.emToken}`;;
            }

            this.pulseAxios.post(path, data, {
                params: params,
                timeout: timeout,
                responseType: respType,
                headers,
                hmac: hmac
            }).then((response) => {
                if (isDefined(response) && isDefined(response.status)) {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(response.data);
                    } else {
                        reject(new Error('Invalid PulseAPI response code: ' + response.status));
                    }
                } else {
                    reject(new Error('Invalid PulseAPI response: NULL'));
                }

            }).catch((error) => {
                reject(error);
            });
        });
    }

    putData(path, params, data, timeout, reqType, responseType, hmac) {
        return new Promise((resolve, reject) => {
            const headers = { };

            if (reqType)
            {
                headers['Content-Type'] = reqType;
            }

            if (!isNullOrWhiteSpace(this.emToken) ) {
                headers['Authorization'] = `Bearer ${this.emToken}`;;
            }

            this.pulseAxios.put(path, data, {
                params,
                timeout,
                responseType,
                headers,
                hmac
            }).then((response) => {
                if (isDefined(response) && isDefined(response.status)) {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(response.data);
                    } else {
                        reject(new Error('Invalid PulseAPI response code: ' + response.status));
                    }
                } else {
                    reject(new Error('Invalid PulseAPI response: NULL'));
                }

            }).catch((error) => {
                reject(error);
            });
        });
    }

    delete(path, params, data, timeout, reqType, respType, hmac) {
        return new Promise((resolve, reject) => {
            const headers = { };
            if (reqType) {
                headers['Content-Type'] = reqType;
            }

            if (!isNullOrWhiteSpace(this.emToken) ) {
                headers['Authorization'] = `Bearer ${this.emToken}`;;
            }

            this.pulseAxios.delete(path, data, {
                params: params,
                timeout: timeout,
                responseType: respType,
                headers,
                hmac: hmac
            }).then((response) => {
                if (isDefined(response) && isDefined(response.status)) {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(response.data);
                    } else {
                        reject(new Error('Invalid PulseAPI response code: ' + response.status));
                    }
                } else {
                    reject(new Error('Invalid PulseAPI response: NULL'));
                }

            }).catch((error) => {
                reject(error);
            });
        });
    }
}
